import type { ExamSession } from "../types/exam-session";

/**
 * 타임스탬프(노트 작성 시각 등)가 어느 시험 회차 구간에 속하는지 판별한다. (JCG-2059)
 *
 * 회차 경계는 `activatedAt`(없으면 `createdAt`) 오름차순으로 정렬해
 * `[해당 회차 시작, 다음 회차 시작)` 구간으로 매핑한다.
 * (badgeActions.ts의 getUserBadgesForSession 구간 매칭과 동일한 규칙)
 *
 * "server-only"가 아니므로 클라이언트에서도 사용 가능.
 *
 * @returns 매칭된 ExamSession, 없으면 null
 */
export function resolveExamSessionForTimestamp(
  sessions: ExamSession[],
  ts: number
): ExamSession | null {
  if (!sessions || sessions.length === 0 || !ts) return null;

  const sorted = [...sessions].sort((a, b) => {
    const startDiff =
      (a.activatedAt || a.createdAt || 0) -
      (b.activatedAt || b.createdAt || 0);
    if (startDiff !== 0) return startDiff;

    // seed/backfill 과정에서 두 회차의 시작 시각이 같아도 회차 순서를 결정적으로 유지한다.
    // 같은 경계 이후의 노트는 더 최신 회차에 속해야 하므로 1회 → 2회 순으로 배치한다.
    const yearDiff = a.year - b.year;
    if (yearDiff !== 0) return yearDiff;

    const roundDiff = a.round - b.round;
    if (roundDiff !== 0) return roundDiff;

    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  for (let i = 0; i < sorted.length; i++) {
    const lower = sorted[i].activatedAt || sorted[i].createdAt || 0;
    const next = sorted[i + 1];
    const upper = next ? next.activatedAt || next.createdAt || Infinity : Infinity;
    if (ts >= lower && ts < upper) {
      return sorted[i];
    }
  }

  // ts가 가장 이른 회차 시작 이전이면 가장 이른 회차로 폴백
  const earliest = sorted[0];
  if (ts < (earliest.activatedAt || earliest.createdAt || 0)) {
    return earliest;
  }

  return null;
}
